/**
 * Terse builders for content blocks.
 *
 * These exist so a project file reads like an outline rather than like markup:
 *
 * ```ts
 * blocks: [
 *   p('We started from a needs analysis...'),
 *   h('Z-Drive Axis'),
 *   img('/assets/CB_Assem_Z-Axis.png', 'Z-axis assembly', { maxWidth: '850px' }),
 *   bullets(['Plays chess unattended', 'Moves in x, y, and z']),
 * ]
 * ```
 */
import type {
  ContentBlock,
  GalleryBlock,
  ImageBlock,
  ImageRef,
  ListBlock,
  MathBlock,
  NoteBlock,
  PdfBlock,
  QuoteBlock,
  RichText,
  SpecsBlock,
  VideoBlock,
} from '@/types/content'

/** A paragraph. */
export const p = (content: RichText): ContentBlock => ({ kind: 'text', content })

/** A section heading inside a project body. */
export const h = (text: string, level: 3 | 4 = 3): ContentBlock => ({
  kind: 'heading',
  text,
  level,
})

/** A single image. */
export const img = (
  src: string,
  alt: string,
  opts: Omit<Partial<ImageBlock>, 'kind' | 'src' | 'alt'> = {},
): ContentBlock => ({ kind: 'image', src, alt, ...opts })

/** A row/grid of images that belong together. */
export const gallery = (
  images: ImageRef[],
  opts: Omit<Partial<GalleryBlock>, 'kind' | 'images'> = {},
): ContentBlock => ({ kind: 'gallery', images, ...opts })

/** An unordered list, optionally with a lead-in line. */
export const bullets = (
  items: RichText[],
  opts: Omit<Partial<ListBlock>, 'kind' | 'items' | 'ordered'> = {},
): ContentBlock => ({ kind: 'list', items, ...opts })

/** A numbered list. */
export const steps = (
  items: RichText[],
  opts: Omit<Partial<ListBlock>, 'kind' | 'items' | 'ordered'> = {},
): ContentBlock => ({ kind: 'list', items, ordered: true, ...opts })

/** An embedded PDF (reports, certificates). */
export const pdf = (
  src: string,
  title: string,
  opts: Omit<Partial<PdfBlock>, 'kind' | 'src' | 'title'> = {},
): ContentBlock => ({ kind: 'pdf', src, title, ...opts })

/** An embedded YouTube video. Pass the bare video id. */
export const youtube = (id: string, title: string): ContentBlock => ({
  kind: 'video',
  provider: 'youtube',
  src: id,
  title,
})

/** A self-hosted video file under `public/`. */
export const video = (src: string, title: string): ContentBlock => ({
  kind: 'video',
  provider: 'file',
  src,
  title,
})

/** A pull quote. */
export const quote = (
  content: RichText,
  attribution?: string,
): ContentBlock => ({
  kind: 'quote',
  content,
  ...(attribution === undefined ? {} : { attribution }),
} as QuoteBlock)

/** A highlighted callout. */
export const note = (
  content: RichText,
  opts: Omit<Partial<NoteBlock>, 'kind' | 'content'> = {},
): ContentBlock => ({ kind: 'note', content, ...opts })

/** A small table of key/value facts. */
export const specs = (items: SpecsBlock['items']): ContentBlock => ({
  kind: 'specs',
  items,
})

/**
 * A LaTeX equation. Write the source with `String.raw` so backslashes survive:
 *
 * ```ts
 * math(String.raw`MPI = \frac{\sigma_Y}{\rho H_m}`, {
 *   description: 'Material performance index equals yield strength over density times hardness',
 * })
 * ```
 */
export const math = (
  latex: string,
  opts: Omit<Partial<MathBlock>, 'kind' | 'latex'> = {},
): ContentBlock => ({ kind: 'math', latex, ...opts })

/* Inline helpers, for use inside `p(...)` and list items. ------------------ */

/** An inline link. External links get `target="_blank"` and a rel guard. */
export const link = (text: string, href: string, external = true) =>
  ({ kind: 'link' as const, text, href, external })

export const em = (text: string) => ({ kind: 'em' as const, text })
export const strong = (text: string) => ({ kind: 'strong' as const, text })
export const code = (text: string) => ({ kind: 'code' as const, text })

/** Re-export so a content file only ever imports from here. */
export type { ContentBlock, ImageRef, RichText, VideoBlock }
